import {Injectable} from '@angular/core';
import {ModalController, NavController} from '@ionic/angular';
import {UserSessionDto} from '../../Models/User/user-session.interface';
import {AuthApiService} from '../Api/Auth/auth-api.service';
import {PermissionsManagerService} from './permissions-manager.service';
import {ItemModuleDto, ModuleDto} from '../../Models/Module/module.interface';
import {FavoriteModuleDto} from '../../Models/Module/favorite-module.interface';
import {RoutesEnum} from '../../Shared/Enum/routes.enum';
import {FavoritesModulesService} from '../Models/Modules/favorites-modules.service';
import {HapticsImpactManagerService} from '../Platform/Capacitor/haptics-impact-manager.service';
import {ToastManagerService} from '../Platform/Ionic/toast-manager.service';
import {LoaderManagerService} from '../Platform/Ionic/loader-manager.service';
import {ClockInApiService} from '../Api/Rrhh/Clock-in/clock-in-api.service';
import {ClockInForm} from '../../Models/Rrhh/Clock-In/clock-in.model';
import {DashboardsApiService} from '../Api/Dashboards/dashboards-api.service';
import {DashboardDto} from '../../Models/Dashboards/dashboard.interface';
import {CommunicationTypesEnum} from '../../Shared/Enum/communication-types.enum';
import {TranslateService} from '@ngx-translate/core';
import {rehydrateFavoriteModules, toFavoriteModules} from '../../Shared/Utils/favorites.utils';

@Injectable({
  providedIn: 'root'
})
export class ModulesManagerService {

  public modules: ModuleDto[] = [];
  public favoritesItems: ItemModuleDto[] = [];

  public loadedModules: boolean = false;

  protected user!: UserSessionDto | null;

  constructor(
    public favoritesModulesModel: FavoritesModulesService,
    protected auth: AuthApiService,
    protected permissionsManager: PermissionsManagerService,
    protected hapticsImpactManager: HapticsImpactManagerService,
    protected toastCtrl: ToastManagerService,
    protected modalCtrl: ModalController,
    protected navCtrl: NavController,
    protected loadCtrl: LoaderManagerService,
    protected clockInService: ClockInApiService,
    protected dashboardsService: DashboardsApiService,
    protected translate: TranslateService,
  ) {
  }


  /**
   * Obtener los módulos
   *
   */
  public async getModules() {
    this.modules = await this.initModules();

    this.checkItemsModule();
    this.getDescriptionModule();

    await this.setFavoriteItemsToModules();
    this.loadedModules = true;
  }


  /**
   * Limpiar datos de los módulos
   *
   */
  public cleanModulesData() {
    this.modules = [];
    this.favoritesItems = [];

    this.loadedModules = false;
  }


  /**
   * Abrir modal para establecer los módulos favoritos
   *
   */
  public async openModalSetFavoritesModules() {
    const {ModulesStorePage} = await import('../../Pages/Modules/modules-store/modules-store.page');

    await this.hapticsImpactManager.hapticsImpactMedium();

    const modal = await this.modalCtrl.create({
      component: ModulesStorePage,
      cssClass: 'yu-modal',
    });

    await modal.present();
  }


  /**
   * Añadir un acceso a favoritos
   *
   */
  public async addFavoriteItem(item: ItemModuleDto) {
    this.favoritesItems = [...this.favoritesItems, {...item, favorite: true}];
    await this.saveFavoriteItems();
  }


  /**
   * Eliminar un acceso de favoritos
   *
   */
  public async removeFavoriteItem(id: string) {
    this.favoritesItems = this.favoritesItems.filter((item) => item.id !== id);
    await this.saveFavoriteItems();
  }


  /**
   * Guardar el orden de los accesos favoritos
   *
   */
  public async saveFavoritesOrder(items: ItemModuleDto[]) {
    this.favoritesItems = items;
    await this.saveFavoriteItems();
  }


  /**
   * Eliminar todos los accesos de favoritos
   *
   */
  public async clearFavoriteItems() {
    this.favoritesItems = [];
    await this.saveFavoriteItems();
  }


  /**
   * Asignar los items favoritos a los módulos
   *
   */
  public async setFavoriteItemsToModules() {
    const storedItems: FavoriteModuleDto[] | null = await this.favoritesModulesModel.getForUser();

    this.favoritesItems = this.rehydrateFavorites(storedItems ?? await this.applyDefaultItems());

    const favoritesItemsIds = new Set(this.favoritesItems.map((favItem) => favItem.id));

    this.modules = this.modules.map((module) => ({
      ...module,
      items: module.items.map((item) => ({
        ...item,
        favorite: favoritesItemsIds.has(item.id),
      }))
    }));
  }


  /**
   * Hacer acción sobre un módulo
   *
   */
  public doModuleAction(module: ModuleDto) {

    if (!module.active) {
      return;
    }

    if (module.items.length === 1) {
      this.doModuleItemAction(module.items[0]);
      return;
    }

    this.openModuleItems(module);
  }


  /**
   * Abrir los items del módulo
   *
   */
  public async openModuleItems(module: ModuleDto) {
    const {ModulesItemsPage} = await import('../../Pages/Modules/modules-items/modules-items.page');
    
    await this.hapticsImpactManager.hapticsImpactMedium();

    const modal = await this.modalCtrl.create({
      component: ModulesItemsPage,
      componentProps: {
        module
      },
      cssClass: 'yu-modal options-modal',
    });

    modal.onDidDismiss().then((data) => {

      const item = data.data;

      if (item) {
        this.doModuleItemAction(item);
      }

    });

    await modal.present();
  }


  /**
   * Hacer acción sobre un item del módulo
   *
   */
  public doModuleItemAction(item: ItemModuleDto) {
    const targetModule = this.modules.find((module) => module.items.some((i) => i.id === item.id));
    const targetItem = targetModule?.items.find((i) => i.id === item.id);

    if (!targetItem) {
      return;
    }

    if (!targetItem.hasPermission) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.NO_PERMISSION'));
      return;
    }

    if (targetItem.action) {
      targetItem.action();
      return;
    }

    if (!targetItem.route) {
      return;
    }

    this.goToView(targetItem.route, targetItem.data);
  }


  /**
   * Ir a la vista
   *
   */
  private goToView(path: string, ...params: any[]) {
    this.navCtrl.navigateForward(path, {
      animated: true,
      queryParams: params ? params[0] : null,
      queryParamsHandling: 'merge'
    });
  }


  /**
   * Obtener los paneles dinámicos
   *
   */
  private async getDashboards(): Promise<ItemModuleDto[]> {
    let dashboards: DashboardDto[] = await this.dashboardsService.model.list() || [];

    return dashboards.map((dashboard, index) => ({
      id: 'dashboards-' + (index + 1),
      name: dashboard.name,
      icon: dashboard.icon && dashboard.icon !== '0' ? dashboard.icon : 'chart-line',
      data: dashboard.data,
      route: RoutesEnum.dashboards,
      hasPermission: true,
    }));
  }


  /**
   * Obtener la descripción de cada módulo
   *
   */
  private getDescriptionModule() {
    this.modules.forEach((module) => {
      const itemsName = module.items.map((item) => item.name);
      module.description = this.transformItemsName(itemsName);
    });
  }


  /**
   * Juntar los nombres de los items de un módulo
   *
   */
  private transformItemsName(itemsName: any[]): string {

    if (itemsName.length === 0) {
      return '';
    }

    if (itemsName.length === 1) {
      return itemsName[0];
    }

    return itemsName.slice(0, -1).join(', ') + ' y ' + itemsName[itemsName.length - 1];
  }


  /**
   * Comprobar si algún módulo no tiene items o no está activo
   *
   */
  private checkItemsModule() {
    this.modules = this.modules
      .filter((module) => module.active && module.items?.length > 0)
      .map((module) => ({
        ...module,
        active: module.items.some((item) => item.hasPermission),
        items: module.items.filter((item) => !item.hidden),
      }));
  }


  /**
   * Asignar y guardar los accesos predeterminados a un usuario que aún no tiene configuración
   *
   */
  private async applyDefaultItems(): Promise<FavoriteModuleDto[]> {
    const defaults: FavoriteModuleDto[] = [];

    await this.favoritesModulesModel.saveForUser(defaults);
    return defaults;
  }


  /**
   * Re-hidratar los favoritos guardados
   *
   */
  private rehydrateFavorites(storedItems: FavoriteModuleDto[]): ItemModuleDto[] {
    return rehydrateFavoriteModules(storedItems, this.modules.flatMap((module) => module.items));
  }


  /**
   * Guardar los favoritos del usuario
   *
   */
  private saveFavoriteItems(): Promise<FavoriteModuleDto[]> {
    return this.favoritesModulesModel.saveForUser(toFavoriteModules(this.favoritesItems));
  }


  /**
   * Inicializar módulos
   *
   */
  private async initModules(): Promise<ModuleDto[]> {
    const dashboards: ItemModuleDto[] = await this.getDashboards();

    this.user = this.auth.getUserSession();

    return [
      {
        id: 'ocr',
        active: true,
        name: this.translate.instant('MODULES.MENU.OCR'),
        icon: 'sparkle',
        items: [
          {
            id: 'ocr-' + 1,
            name: this.translate.instant('MODULES.MENU.DOCUMENTS_REVIEW'),
            icon: 'file-magnifying-glass',
            route: RoutesEnum.documentsReview,
            hasPermission: this.permissionsManager.checkPermission('revision_albaranes', 'ver')
              || this.permissionsManager.checkPermission('revision_gastos', 'ver')
              || this.permissionsManager.checkPermission('revision_nominas', 'ver')
              || this.permissionsManager.checkPermission('revision_otros', 'ver'),
          }
        ],
      },
      {
        id: 'events',
        active: true,
        name: this.translate.instant('MODULES.MENU.EVENTS'),
        icon: 'ticket',
        items: [
          {
            id: 'events-' + 1,
            name: this.translate.instant('MODULES.MENU.EVENTS'),
            icon: 'calendar',
            route: RoutesEnum.events,
            hasPermission: this.permissionsManager.checkPermission('event_config', 'ver'),
          },
        ]
      },
      {
        id: 'purchases',
        active: true,
        name: this.translate.instant('MODULES.MENU.PURCHASES'),
        icon: 'shopping-cart-simple',
        items: [
          {
            id: 'purchases-' + 1,
            name: this.translate.instant('MODULES.MENU.ORDERS'),
            icon: 'shopping-cart-simple',
            route: RoutesEnum.orders,
            hasPermission: this.permissionsManager.checkPermission('pedidos', 'ver'),
          },
          {
            id: 'purchases-' + 2,
            name: this.translate.instant('MODULES.MENU.DELIVERY_NOTES'),
            icon: 'file-text',
            route: RoutesEnum.deliveryNotes,
            hasPermission: this.permissionsManager.checkPermission('albaranes', 'ver'),
          },
          {
            id: 'purchases-' + 3,
            name: this.translate.instant('MODULES.MENU.PROVIDERS'),
            icon: 'suitcase-simple',
            route: RoutesEnum.providers,
            hasPermission: this.permissionsManager.checkPermission('proveedores', 'ver'),
          },
          {
            id: 'purchases-' + 4,
            name: this.translate.instant('MODULES.MENU.PRODUCTS'),
            icon: 'bread',
            route: RoutesEnum.products,
            hasPermission: this.permissionsManager.checkPermission('productos', 'ver'),
          }
        ],
      },
      {
        id: 'yuchef',
        active: true,
        name: this.translate.instant('MODULES.MENU.YUCHEF'),
        icon: 'chef-hat',
        items: [
          {
            id: 'yuchef-' + 1,
            name: this.translate.instant('MODULES.MENU.MISE_EN_PLACE'),
            icon: 'cooking-pot',
            route: RoutesEnum.miseEnPlace,
            hasPermission: this.permissionsManager.checkPermission('miseenplace', 'ver'),
          },
          {
            id: 'yuchef-' + 2,
            name: this.translate.instant('MODULES.MENU.LABELED'),
            icon: 'printer',
            route: RoutesEnum.labeled,
            hasPermission: this.permissionsManager.checkPermission('etiquetado', 'ver'),
          },
          {
            id: 'yuchef-' + 3,
            name: this.translate.instant('MODULES.MENU.RECIPE_SHEETS'),
            icon: 'fork-knife',
            route: RoutesEnum.recipesCosting,
            hasPermission: this.permissionsManager.checkPermission('escandallos', 'ver'),
          },
          {
            id: 'yuchef-' + 4,
            name: this.translate.instant('MODULES.MENU.ELABORATIONS'),
            icon: 'knife',
            route: RoutesEnum.elaborations,
            hasPermission: this.permissionsManager.checkPermission('elaborados', 'ver'),
          },
          {
            id: 'yuchef-' + 5,
            name: this.translate.instant('MODULES.MENU.RECIPES'),
            icon: 'hamburger',
            route: RoutesEnum.recipes,
            hasPermission: this.permissionsManager.checkPermission('recetas', 'ver'),
          },
        ],
      },
      {
        id: 'stocks',
        active: true,
        name: this.translate.instant('MODULES.MENU.WAREHOUSES'),
        icon: 'package',
        items: [
          {
            id: 'stocks-' + 1,
            name: this.translate.instant('MODULES.MENU.WAREHOUSE'),
            icon: 'warehouse',
            route: RoutesEnum.stocks,
            hasPermission: this.permissionsManager.checkPermission('almacen', 'ver'),
          },
          {
            id: 'stocks-' + 2,
            name: this.translate.instant('MODULES.MENU.WASTAGES'),
            icon: 'trash-simple',
            route: RoutesEnum.wastage,
            hasPermission: this.permissionsManager.checkPermission('mermas', 'ver'),
          },
          {
            id: 'stocks-' + 3,
            name: this.translate.instant('MODULES.MENU.TRANSFERS'),
            icon: 'repeat',
            route: RoutesEnum.transfers,
            hasPermission: this.permissionsManager.checkPermission('traslados', 'ver'),
          },
          {
            id: 'stocks-' + 4,
            name: this.translate.instant('MODULES.MENU.OPERATIONS'),
            icon: 'notepad',
            route: RoutesEnum.operations,
            hasPermission: this.permissionsManager.checkPermission('inventarios', 'ver'),
          },
        ],
      },
      {
        id: 'rrhh',
        active: true,
        name: this.translate.instant('MODULES.MENU.HR'),
        icon: 'users-three',
        items: [
          {
            id: 'rrhh-' + 1,
            name: this.translate.instant('MODULES.MENU.MY_SCHEDULE'),
            icon: 'calendar',
            route: RoutesEnum.schedules,
            hasPermission: this.permissionsManager.checkPermission('planificacion', 'ver'),
          },
          {
            id: 'rrhh-' + 2,
            name: this.translate.instant('MODULES.MENU.CLOCK_IN'),
            icon: 'fingerprint-simple',
            highlight: !!this.user?.fichaje,
            hasPermission: this.permissionsManager.checkPermission('fichajes', 'anadir'),
            action: () => this.openKeyboard(),
          },
          {
            id: 'rrhh-' + 3,
            name: this.translate.instant('MODULES.MENU.TIME_TRACKING'),
            icon: 'clock',
            route: RoutesEnum.clockIn,
            hasPermission: this.permissionsManager.checkPermission('fichajes', 'ver'),
          },
          {
            id: 'rrhh-' + 4,
            name: this.translate.instant('MODULES.MENU.HOLIDAYS'),
            icon: 'airplane-in-flight',
            route: RoutesEnum.holidays,
            hasPermission: this.permissionsManager.checkPermission('vacaciones', 'ver'),
          },
          {
            id: 'rrhh-' + 5,
            name: this.translate.instant('MODULES.MENU.PUBLIC_HOLIDAYS'),
            icon: 'calendar-blank',
            route: RoutesEnum.publicHolidays,
            hasPermission: this.permissionsManager.checkPermission('festivos', 'ver'),
          },
          {
            id: 'rrhh-' + 6,
            name: this.translate.instant('MODULES.MENU.DOCUMENTS'),
            icon: 'folder',
            route: RoutesEnum.folders,
            hasPermission: this.permissionsManager.checkPermission('carpetas', 'ver'),
          },
          {
            id: 'rrhh-' + 7,
            name: this.translate.instant('MODULES.MENU.REQUESTS'),
            icon: 'files',
            route: RoutesEnum.requests,
            hasPermission: this.permissionsManager.checkPermission('solicitudes', 'ver'),
          },
        ],
      },
      {
        id: 'finances',
        active: true,
        name: this.translate.instant('MODULES.MENU.FINANCES'),
        icon: 'chart-line-up',
        items: [
          {
            id: 'finances-' + 1,
            name: this.translate.instant('MODULES.MENU.CASH_CLOSINGS'),
            icon: 'cardholder',
            route: RoutesEnum.cashClosing,
            hasPermission: this.permissionsManager.checkPermission('cierres', 'ver'),
          },
          {
            id: 'finances-' + 2,
            name: this.translate.instant('MODULES.MENU.PAYMENTS'),
            icon: 'hand-coins',
            route: RoutesEnum.payments,
            hasPermission: this.permissionsManager.checkPermission('pagos', 'ver'),
          },
          {
            id: 'finances-' + 3,
            name: this.translate.instant('MODULES.MENU.EXPENSES'),
            icon: 'receipt',
            route: RoutesEnum.expenses,
            hasPermission: this.permissionsManager.checkPermission('gastos', 'ver'),
          }
        ],
      },
      {
        id: 'communication',
        active: true,
        name: this.translate.instant('MODULES.MENU.COMMUNICATION'),
        icon: 'chats-teardrop',
        items: [
          {
            id: 'communication-' + 1,
            name: this.translate.instant('MODULES.MENU.CHATS'),
            icon: 'chat-circle-dots',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.chats},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('chats', 'ver'),
          },
          {
            id: 'communication-' + 2,
            name: this.translate.instant('MODULES.MENU.BREAKDOWNS'),
            icon: 'wrench',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.breakdowns},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('averias', 'ver'),
          },
          {
            id: 'communication-' + 3,
            name: this.translate.instant('MODULES.MENU.MEETINGS'),
            icon: 'users-three',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.meetings},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('reuniones', 'ver'),
          },
          {
            id: 'communication-' + 4,
            name: this.translate.instant('MODULES.MENU.COMMUNICATION_EVENTS'),
            icon: 'confetti',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.events},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('eventos', 'ver'),
          },
          {
            id: 'communication-' + 5,
            name: this.translate.instant('MODULES.MENU.NOTICES'),
            icon: 'megaphone',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.warnings},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('avisos', 'ver'),
          },
          {
            id: 'communication-' + 6,
            name: this.translate.instant('MODULES.MENU.LISTS'),
            icon: 'list-bullets',
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.lists},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver') && this.permissionsManager.checkPermission('listas', 'ver'),
          }
        ],
      },
      {
        id: 'checklists',
        active: true,
        name: this.translate.instant('MODULES.MENU.CHECKLISTS'),
        icon: 'check-circle',
        items: [
          {
            id: 'checklists-' + 1,
            name: this.translate.instant('MODULES.MENU.CHECKLISTS'),
            icon: 'calendar-dots',
            route: RoutesEnum.tasks,
            data: {type: 'checklist'},
            hasPermission: this.permissionsManager.checkPermission('checklists', 'ver'),
          },
          {
            id: 'checklists-' + 2,
            name: this.translate.instant('MODULES.MENU.TEMPLATES'),
            icon: 'files',
            route: RoutesEnum.models,
            data: {type: 'checklist'},
            hasPermission: this.permissionsManager.checkPermission('checklists', 'ver'),
          },
          {
            id: 'checklists-' + 3,
            name: this.translate.instant('MODULES.MENU.SCHEDULED'),
            icon: 'faders-horizontal',
            route: RoutesEnum.schedulesChecklist,
            data: {type: 'checklist'},
            hasPermission: this.permissionsManager.checkPermission('checklists', 'ver'),
          },
        ],
      },
      {
        id: 'audits',
        active: true,
        name: this.translate.instant('MODULES.MENU.AUDITS'),
        icon: 'list-star',
        items: [
          {
            id: 'audits-' + 1,
            name: this.translate.instant('MODULES.MENU.AUDITS'),
            icon: 'calendar-dots',
            route: RoutesEnum.tasks,
            data: {type: 'audit'},
            hasPermission: this.permissionsManager.checkPermission('auditorias', 'ver'),
          },
          {
            id: 'audits-' + 2,
            name: this.translate.instant('MODULES.MENU.TEMPLATES'),
            icon: 'files',
            route: RoutesEnum.models,
            data: {type: 'audit'},
            hasPermission: this.permissionsManager.checkPermission('auditorias', 'ver'),
          },
          {
            id: 'audits-' + 3,
            name: this.translate.instant('MODULES.MENU.SCHEDULED'),
            icon: 'faders-horizontal',
            route: RoutesEnum.schedulesChecklist,
            data: {type: 'audit'},
            hasPermission: this.permissionsManager.checkPermission('auditorias', 'ver'),
          },
        ],
      },
      {
        id: 'appcc',
        active: true,
        name: this.translate.instant('MODULES.MENU.APPCC'),
        icon: 'list-checks',
        items: [
          {
            id: 'appcc-' + 1,
            name: this.translate.instant('MODULES.MENU.APPCC'),
            icon: 'calendar-dots',
            route: RoutesEnum.tasks,
            data: {type: 'appcc'},
            hasPermission: this.permissionsManager.checkPermission('appcc', 'ver'),
          },
          {
            id: 'appcc-' + 2,
            name: this.translate.instant('MODULES.MENU.CONTROL_POINTS'),
            icon: 'stamp',
            route: RoutesEnum.checkpoints,
            hasPermission: this.permissionsManager.checkPermission('appcc', 'ver'),
          },
          {
            id: 'appcc-' + 3,
            name: this.translate.instant('MODULES.MENU.SCHEDULED'),
            icon: 'faders-horizontal',
            route: RoutesEnum.schedulesAppcc,
            hasPermission: this.permissionsManager.checkPermission('appcc', 'ver'),
          },
          {
            id: 'appcc-' + 4,
            name: this.translate.instant('MODULES.MENU.INCIDENTS'),
            icon: 'warning-circle',
            route: '',
            hasPermission: false,
            hidden: true,
          },
        ],
      },
      {
        id: 'reports',
        active: true,
        name: this.translate.instant('MODULES.MENU.REPORTS'),
        icon: 'chart-bar',
        items: [
          {
            id: 'reports-' + 1,
            name: this.translate.instant('MODULES.MENU.PRODUCT_PURCHASES'),
            icon: 'bread',
            route: RoutesEnum.productPurchasesReport,
            hasPermission: this.permissionsManager.checkPermission('informe_compras_producto', 'ver'),
          },
          {
            id: 'reports-' + 2,
            name: this.translate.instant('MODULES.MENU.PROVIDER_PURCHASES'),
            icon: 'suitcase-simple',
            route: RoutesEnum.providerPurchasesReport,
            hasPermission: this.permissionsManager.checkPermission('informe_compras_proveedor', 'ver'),
          },
          {
            id: 'reports-' + 3,
            name: this.translate.instant('MODULES.MENU.CHECKLISTS'),
            icon: 'check-circle',
            route: RoutesEnum.checklistsReport,
            hasPermission: this.permissionsManager.checkPermission('informe_checklists', 'ver'),
          },
          {
            id: 'reports-' + 4,
            name: this.translate.instant('MODULES.MENU.AUDITS'),
            icon: 'list-star',
            route: RoutesEnum.auditsReport,
            hasPermission: this.permissionsManager.checkPermission('informe_auditorias', 'ver'),
          },
          {
            id: 'reports-' + 5,
            name: this.translate.instant('MODULES.MENU.APPCC'),
            icon: 'list-checks',
            route: RoutesEnum.appccsReport,
            hasPermission: this.permissionsManager.checkPermission('informe_appccs', 'ver'),
          },
          {
            id: 'reports-' + 6,
            name: this.translate.instant('MODULES.MENU.EBITDA'),
            icon: 'chart-line',
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
          {
            id: 'reports-' + 7,
            name: this.translate.instant('MODULES.MENU.TIME_TRACKING_REPORT'),
            icon: 'clock',
            route: RoutesEnum.clockInReport,
            hasPermission: this.permissionsManager.checkPermission('fichajes_globales', 'ver'),
          },
        ],
      },
      {
        id: 'dashboards',
        active: this.permissionsManager.checkPermission('dashboards_dinamicos', 'ver'),
        name: this.translate.instant('MODULES.MENU.DYNAMIC_DASHBOARDS'),
        icon: 'presentation-chart',
        items: dashboards || [],
      },
      {
        id: 'configuration',
        active: true,
        name: this.translate.instant('MODULES.MENU.CONFIGURATION'),
        icon: 'gear',
        items: [
          {
            id: 'configuration-' + 1,
            name: this.translate.instant('MODULES.MENU.PROFILE'),
            icon: 'user',
            route: RoutesEnum.profile,
            hasPermission: this.permissionsManager.checkPermission('configuracion', 'ver'),
            hidden: true,
          },
          {
            id: 'configuration-' + 2,
            name: this.translate.instant('MODULES.MENU.DEVICES'),
            icon: 'printer',
            route: RoutesEnum.devices,
            hasPermission: this.permissionsManager.checkPermission('dispositivos', 'ver'),
          },
          {
            id: 'configuration-' + 3,
            name: this.translate.instant('MODULES.MENU.LOCALS'),
            icon: 'storefront',
            route: RoutesEnum.locals,
            hasPermission: this.permissionsManager.checkPermission('locales', 'ver'),
          },
          {
            id: 'configuration-' + 4,
            name: this.translate.instant('MODULES.MENU.EMPLOYEES'),
            icon: 'users',
            route: RoutesEnum.users,
            hasPermission: this.permissionsManager.checkPermission('usuarios', 'ver'),
          },
        ],
      },
    ];
  }


  /**
   * Abrir teclado
   *
   */
  private async openKeyboard() {
    const {KeyboardModalPage} = await import('../../Pages/Shared/keyboard-modal/keyboard-modal.page');

    const modalElement = await this.modalCtrl.create({
      component: KeyboardModalPage,
      cssClass: 'yu-modal',
    });

    modalElement.onDidDismiss().then((data) => {
      if (data.data) {
        this.setClockIn();
      }
    });

    await modalElement.present();
  }


  /**
   * Guardar fichaje
   *
   */
  private async setClockIn() {
    const params = new ClockInForm();
    params.id_usuario = this.user?.id;
    params.pin = this.user?.pin;

    await this.loadCtrl.show();

    this.clockInService.setClockIn(params).then((response) => {

      this.toastCtrl.showToastByStatusCode(response.status, response.mensaje);

      if (response.status !== 1) {
        return;
      }

      this.user = this.auth.setUserSession(response.data);
      this.updateClockInHighlight();

    }).finally(() => {
      this.loadCtrl.hide();
    });
  }


  /**
   * Actualiza el estado de resaltado del botón de fichaje
   *
   */
  async updateClockInHighlight() {
    this.favoritesItems = this.favoritesItems.map((item) =>
      item.id === 'rrhh-2' ? {...item, highlight: !!this.user?.fichaje} : item
    );

    await this.favoritesModulesModel.save(this.favoritesItems);
    await this.getModules();
  }

}
